import qs from 'qs';
import { get, post } from './';

const sanitizeRoom = room => ({
  adults: room?.adults,
  ...(room?.children !== undefined && { children: room.children }),
  childrenBirthdates: room?.childrenBirthdates ?? [],
  firstAdultNationality:
    typeof room?.firstAdultNationality === 'string'
      ? room.firstAdultNationality
      : (room?.firstAdultNationality?.value ?? ''),
});

// Each composition entry is one physical room. Drop the legacy `count` field
// (it holds pax, not a room multiplier) and sanitize 1:1.
const formatRooms = (rooms = []) =>
  // eslint-disable-next-line no-unused-vars
  (rooms ?? []).map(({ count, ...room }) => sanitizeRoom(room));

// Translate FE filters into the internal availability query: rename the stay
// window, expand rooms, drop fields the backend now derives from the trip.
// KD-3977: `scopeDmcId` is the DMC scope of a search without a trip (experience
// marketplace). `dmcId` stays dropped: trip searches get it from their trip.
const toAvailabilityQuery = ({
  startAt,
  endAt,
  rooms,
  bypassLocationRequirement,
  dmcId,
  scopeDmcId,
  includePartnerHotels,
  hasTemplatePrice,
  ...rest
} = {}) => ({
  ...rest,
  ...(scopeDmcId ? { dmcId: scopeDmcId } : {}),
  checkIn: startAt,
  checkOut: endAt,
  rooms: formatRooms(rooms),
});

const buildQuery = filters =>
  qs.stringify(toAvailabilityQuery(filters), {
    arrayFormat: 'indices',
    skipNulls: true,
  });

// qs drops an empty `rooms` array entirely, so a caller that mounts before the
// user has picked occupancy (HotelsDetail has no `skip`) would send a roomsless
// URL the BFF can only answer with a 400. Treat it as "not ready", same as skip.
const hasOccupancy = filters =>
  (filters?.rooms ?? []).some(room => Number(room?.adults) > 0);

// Catalog listing (POST /api/v2/hotels) — full hotel list, no dates required.
// Availability search uses getHotelsAvailability below.
export const getHotels = ({ filters, locale, skip = false }) => {
  if (skip) {
    return Promise.resolve(null);
  }

  return post(`/api/v2/hotels?locale=${locale ?? 'en'}`, filters);
};

export const getHotelsAvailability = ({ filters, locale, skip = false }) => {
  if (skip || !hasOccupancy(filters)) {
    return Promise.resolve(null);
  }

  return get(
    `/api/internal/hotels/availability?locale=${locale ?? 'en'}&${buildQuery(
      filters,
    )}`,
  );
};

// The internal rooms DTO nests supplier:{name,code,flux:{name,code,logo}|null};
// downstream consumers (roomKeyGenerator, booking tunnel, checkout) read the flat
// supplier/supplierCode/source/sourceLogo, so flatten here at the one seam. The
// old top-level `source`/`sourceLogo` are now the supplier's `flux` — the Koedia
// trigramme that sold the rate (Expedia, Sunhotels, Shangri La, ...); null off
// Koedia. `?? flat` keeps it working against a not-yet-nested API response.
const flattenSupplierSource = possibility => ({
  ...possibility,
  supplier: possibility?.supplier?.name ?? possibility?.supplier,
  supplierCode: possibility?.supplier?.code ?? possibility?.supplierCode,
  source: possibility?.supplier?.flux?.name ?? possibility?.source,
  sourceLogo: possibility?.supplier?.flux?.logo ?? possibility?.sourceLogo,
  compatibleRoomCompositions: (
    possibility?.compatibleRoomCompositions ?? []
  ).map(flattenSupplierSource),
});

export const getRoomsAvailability = ({ id, filters, locale }) =>
  get(
    `/api/internal/hotels/${id}/rooms/availability?locale=${
      locale ?? 'en'
    }&${buildQuery(filters)}`,
  ).then(res => {
    const env = res?.data ?? res;
    // Internal envelope: { requestId, expiresAt, data: [...possibilities] }.
    return {
      requestId: env?.requestId,
      expiresAt: env?.expiresAt,
      possibilities: (env?.data ?? []).map(flattenSupplierSource),
    };
  });

export const getHotel = async ({ hotelId, locale, skip }) => {
  if (skip || !hotelId) {
    return Promise.resolve(null);
  }

  return get(`/api/v1/hotels/${hotelId}?locale=${locale ?? 'en'}`);
};

export const submitBook = ({ id, data }) => {
  return post(`/api/v1/hotels/${id}/book`, data);
};
